/**
 * The `$1,700` tag on a task card — `studio/tasks.html`'s
 * `<span class="tag num push">`.
 *
 * A `Tag` with a formatted amount in it, and the reason it is a component rather than
 * two lines at each of the six call sites is the `push`: the tag sits at the right of
 * the card's meta row, and a card that forgot the spacer put the money next to the
 * avatar. `Push` is a spacer **element** (session 09's `Layout.tsx`), so it has to be
 * rendered rather than passed as a prop, and forgetting it is invisible until
 * somebody looks at a card with a short due date on it.
 *
 * `note` is the one thing the mockup varies: `$85 extra`, `+$240/mo`, `€1,780`. The
 * amount is always `format.money`, so a currency is never assumed and `€1,780` is not
 * a string somebody typed.
 */

import type { Money } from '@fareast/shared'

import { useFormat } from '../../format/useFormat.js'
import { Push } from '../portal/Layout.js'
import { Tag } from '../portal/Status.js'

export type MoneyTagProps = {
  readonly amount: Money
  /** What follows the amount inside the tag — "extra", "/mo". Nothing by default. */
  readonly note?: string
  /**
   * A leading `+` for a signed figure — a package change's monthly delta.
   *
   * `SubFigure` has no signed rendering either (`.docs/reports/19-report.md`
   * §Deferred), so this is the one place a sign is printed and it is opt-in: a
   * milestone's `$1,419` is not `+$1,419`.
   */
  readonly signed?: boolean
  /** Leave the spacer out — for a tag that is not the last thing in its row. */
  readonly inline?: boolean
}

export function MoneyTag({ amount, note, signed, inline }: MoneyTagProps) {
  const format = useFormat()
  const sign = signed === true && amount.amount > 0 ? '+' : ''
  const text = `${sign}${format.money(amount)}${note === undefined ? '' : ` ${note}`}`

  return (
    <>
      {inline === true ? null : <Push />}
      <Tag>{text}</Tag>
    </>
  )
}
